import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { Icon } from '../lib/icons';

type Step = 'email' | 'answer' | 'password' | 'done';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');

  const [email, setEmail] = useState('');
  const [questionId, setQuestionId] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function fetchQuestion(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const res = await api<{ questionId: string; question: string }>('/security/recover/question', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });

    setLoading(false);

    if (res.ok && res.data) {
      setQuestionId(res.data.questionId);
      setQuestion(res.data.question);
      setStep('answer');
    } else {
      setError(errMsg(res, '未找到该账号的密保问题'));
    }
  }

  async function verifyAnswer(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const res = await api<{ resetToken: string }>('/security/recover/verify', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        questionId,
        answer,
      }),
    });

    setLoading(false);

    if (res.ok && res.data) {
      setResetToken(res.data.resetToken);
      setStep('password');
    } else {
      setError(errMsg(res, '答案不正确'));
    }
  }

  async function resetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (newPwd !== confirmPwd) {
      setError('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    const res = await api('/auth/recover/reset', {
      method: 'POST',
      body: JSON.stringify({ resetToken, newPassword: newPwd }),
    });
    setLoading(false);

    if (res.ok) {
      setStep('done');
    } else {
      setError(errMsg(res, '重置失败，请重新验证密保'));
      if (res.error?.code === 'INVALID_RESET_TOKEN') setStep('email');
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-ink-100 to-ink-50 px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-ink-900 text-lg text-candle-300">
            墓
          </div>
          <h1 className="font-serifcn text-2xl text-ink-900">找回密码</h1>

          <div className="mt-5 flex items-center justify-center gap-2 text-[10px] text-ink-400">
            {['输入邮箱', '回答密保', '设置新密码'].map((label, i) => {
              const active =
                (i === 0 && step === 'email') ||
                (i === 1 && step === 'answer') ||
                (i === 2 && (step === 'password' || step === 'done'));
              return (
                <span key={label} className="flex items-center gap-2">
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full ${
                      active ? 'bg-ink-900 text-ink-50' : 'bg-ink-200 text-ink-500'
                    }`}
                  >
                    {i + 1}
                  </span>
                  {label}
                  {i < 2 && <span className="text-ink-300">—</span>}
                </span>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-ink-200 bg-white/80 p-6 backdrop-blur">
          {step === 'email' && (
            <form onSubmit={fetchQuestion}>
              <label className="mb-1.5 block text-xs text-ink-600">注册邮箱</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
              />
              <p className="mt-2 text-[11px] text-ink-400">
                我们会从你设置的密保问题里随机抽一个来验证身份
              </p>
              {renderError()}
              <button
                type="submit"
                disabled={loading || !email}
                className="mt-5 h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-40"
              >
                {loading ? '查询中…' : '下一步'}
              </button>
            </form>
          )}

          {step === 'answer' && (
            <form onSubmit={verifyAnswer}>
              <p className="mb-4 rounded-xl bg-ink-50 px-4 py-3 font-serifcn text-base text-ink-900">
                「{question}」
              </p>
              <label className="mb-1.5 block text-xs text-ink-600">你的答案</label>
              <input
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="输入设置时的答案"
                required
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
              />
              <p className="mt-2 text-[11px] text-ink-400">
                答案不区分大小写与空格，但需与设置时一致
              </p>
              {renderError()}
              <button
                type="submit"
                disabled={loading || !answer}
                className="mt-5 h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-40"
              >
                {loading ? '验证中…' : '验证答案'}
              </button>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={resetPassword}>
              <div className="mb-3">
                <label className="mb-1.5 block text-xs text-ink-600">新密码</label>
                <input
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  placeholder="至少 8 位"
                  required
                  className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
                />
              </div>
              <div className="mb-4">
                <label className="mb-1.5 block text-xs text-ink-600">确认新密码</label>
                <input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required
                  className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
                />
              </div>
              <p className="mb-2 text-[11px] text-ink-400">
                重置令牌 15 分钟内有效，超时请重新验证
              </p>
              {renderError()}
              <button
                type="submit"
                disabled={loading || !newPwd}
                className="h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-40"
              >
                {loading ? '重置中…' : '重置密码'}
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center">
              <p className="text-4xl"><Icon name="check" /></p>
              <p className="mt-4 text-sm text-ink-800">密码已重置</p>
              <p className="mt-1 text-xs text-ink-500">请使用新密码登录</p>
              <button
                onClick={() => navigate('/login')}
                className="mt-6 h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50"
              >
                去登录
              </button>
            </div>
          )}

          <div className="mt-5 text-center">
            <Link to="/login" className="text-xs text-ink-500 underline">
              想起密码了？去登录
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  function renderError() {
    if (!error) return null;
    return (
      <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
    );
  }
}
